import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import ApiService from '@/services/api.service';
import BackgroundService from '@/services/bg.service';
import { showToast } from '@/services/toast.service';
import type { PipelineNode } from '@/services/pipelineNode.service';

interface SquashPreview {
  squashable: boolean;
  reason?: string;
  total_rows?: number;
  distinct_rows?: number;
  duplicate_rows?: number;
  composite_key?: string[];
  last_modified_column?: string;
}

interface SquashTabProps {
  node: PipelineNode;
}

export function SquashTab({ node }: SquashTabProps) {
  const [preview, setPreview] = useState<SquashPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [squashing, setSquashing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [squashResult, setSquashResult] = useState<{ rows_removed: number; total_rows_after: number } | null>(null);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSquashResult(null);
    try {
      const result = await ApiService.getInstance().request(
        'GET',
        `/pipelinenodes/${node.id}/squash-preview`
      ) as SquashPreview;
      setPreview(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load squash preview');
    } finally {
      setLoading(false);
    }
  }, [node.id]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  const handleSquash = async () => {
    setConfirmOpen(false);
    setSquashing(true);
    setSquashResult(null);
    try {
      const response = await ApiService.getInstance().request(
        'POST',
        `/pipelinenodes/${node.id}/squash`
      ) as { ok: boolean; job_id: string };

      const result = await BackgroundService.getInstance().waitForJob(response.job_id);
      setSquashResult(result);
      showToast.success('Squash complete', {
        description: `Removed ${(result.rows_removed ?? 0).toLocaleString()} duplicate rows`,
      });
      // Refresh preview to show updated counts
      fetchPreview();
    } catch (e: any) {
      showToast.error('Squash failed', {
        description: e.message || 'An error occurred during squash',
      });
    } finally {
      setSquashing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!preview?.squashable) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>This source is not squashable</AlertTitle>
        <AlertDescription>
          {preview?.reason || 'Unknown reason'}. Configure these in the General tab.
        </AlertDescription>
      </Alert>
    );
  }

  const reductionPct = preview.total_rows && preview.total_rows > 0
    ? ((preview.duplicate_rows ?? 0) / preview.total_rows * 100).toFixed(1)
    : '0';

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Delete Duplicates</CardTitle>
          <CardDescription>
            Remove duplicate rows by keeping only the most recent record for each composite key.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Key configuration */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Composite Key</div>
            <div className="flex flex-wrap gap-1">
              {preview.composite_key?.map((col) => (
                <Badge key={col} variant="secondary">{col}</Badge>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Last Modified Column</div>
            <Badge variant="secondary">{preview.last_modified_column}</Badge>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium text-muted-foreground">Total Rows</div>
              <div className="text-2xl font-bold">{(preview.total_rows ?? 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium text-muted-foreground">Distinct Keys</div>
              <div className="text-2xl font-bold">{(preview.distinct_rows ?? 0).toLocaleString()}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium text-muted-foreground">Duplicate Rows</div>
              <div className="text-2xl font-bold text-destructive">
                {(preview.duplicate_rows ?? 0).toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground">{reductionPct}% of table</div>
            </div>
          </div>

          {squashResult && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Complete</AlertTitle>
              <AlertDescription>
                Removed {squashResult.rows_removed.toLocaleString()} rows.
                Table now has {squashResult.total_rows_after.toLocaleString()} rows.
              </AlertDescription>
            </Alert>
          )}

          {/* Action */}
          <div>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={squashing || (preview.duplicate_rows ?? 0) === 0}
            >
              {squashing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {squashing ? 'Deleting...' : 'Delete Duplicates'}
            </Button>
            {(preview.duplicate_rows ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground mt-2">No duplicates to remove.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Duplicates</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{(preview.duplicate_rows ?? 0).toLocaleString()}</strong> duplicate
              rows, keeping only the most recent record per composite key.
              This operation modifies data directly and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSquash}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Confirm Squash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
